import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { settingsAPI } from '@/lib/api';
import api from '@/lib/api';

// Context para dados globais da aplicação (settings, blog count, etc.)
const AppDataContext = createContext(null);

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

export const AppDataProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [blogCount, setBlogCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs para controle de cache e chamadas
  const settingsLastFetch = useRef(0);
  const blogCountLastFetch = useRef(0);
  const isFetching = useRef(false);
  const hasFetched = useRef(false);

  // Função para buscar settings com cache
  const fetchSettings = useCallback(async (force = false) => {
    const now = Date.now();
    
    // Se já tem dados em cache e não forçou refresh, retorna os dados existentes
    if (!force && settings && (now - settingsLastFetch.current < CACHE_DURATION)) {
      return settings;
    }
    
    try {
      const response = await settingsAPI.get();
      const data = response.data;
      setSettings(data);
      settingsLastFetch.current = now;
      return data;
    } catch (err) {
      console.error('Error fetching settings:', err);
      // Retorna default settings em caso de erro
      const defaultSettings = {
        whatsapp_group_link: 'https://whatsapp.com/channel/0029Vb7oBZaKbYMPWBZkWb2i',
        whatsapp_channel_link: 'https://whatsapp.com/channel/0029Vb7oBZaKbYMPWBZkWb2i',
        auto_redirect_enabled: false,
        auto_redirect_delay: 3,
        site_name: 'Carvalho Comercio',
        site_description: 'As melhores ofertas da internet',
        instagram_url: 'https://www.instagram.com/carvalhocomercio',
        email_contact: 'carvalhocomercio7@gmail.com',
        hero_title: 'Bem-vindo aos Melhores Achadinhos!',
        hero_subtitle: 'Ofertas imperdiveis para voce!',
        hero_gradient_start: '#FF6B00',
        hero_gradient_end: '#FFD700',
        urgency_banner_enabled: true,
        urgency_banner_text: 'OFERTAS RELAMPAGO EXPIRAM EM POUCOS MINUTOS!',
        vip_group_link: 'https://whatsapp.com/channel/0029Vb7oBZaKbYMPWBZkWb2i',
        shopee_url: 'https://collshp.com/carvalhocomercio?view=storefront'
      };
      if (!settings) {
        setSettings(defaultSettings);
      }
      return settings || defaultSettings;
    }
  }, [settings]);

  // Função para buscar blog count com cache
  const fetchBlogCount = useCallback(async (force = false) => {
    const now = Date.now();
    
    // Se já tem dados em cache e não forçou refresh, retorna
    if (!force && blogCount !== null && (now - blogCountLastFetch.current < CACHE_DURATION)) {
      return blogCount;
    }
    
    try {
      const response = await api.get('/blog/count');
      const count = response.data?.count || 0;
      setBlogCount(count);
      blogCountLastFetch.current = now;
      return count;
    } catch (err) {
      console.error('Error fetching blog count:', err);
      return blogCount;
    }
  }, [blogCount]);

  // Busca inicial de todos os dados (uma única vez)
  const fetchAllData = useCallback(async () => {
    // Evita chamadas duplicadas
    if (isFetching.current || hasFetched.current) return;
    isFetching.current = true;
    
    setLoading(true);
    setError(null);
    
    try {
      // Busca em paralelo
      await Promise.all([
        fetchSettings(true),
        fetchBlogCount(true)
      ]);
      hasFetched.current = true;
    } catch (err) {
      console.error('Error fetching app data:', err);
      setError(err);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [fetchSettings, fetchBlogCount]);

  // Busca inicial ao montar o provider
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Função para forçar refresh dos dados
  const refreshData = useCallback(async () => {
    hasFetched.current = false;
    isFetching.current = false;
    await fetchAllData();
  }, [fetchAllData]);

  // Função para atualizar settings (após edição no admin)
  const updateSettings = useCallback((newSettings) => {
    setSettings(newSettings);
    settingsLastFetch.current = Date.now();
  }, []);

  const value = {
    // Dados
    settings,
    blogCount,
    loading,
    error,
    
    // Funções
    refreshData,
    updateSettings,
    fetchSettings,
    fetchBlogCount
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};

// Hook para usar o contexto
export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export default AppDataContext;
